import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, 
  Eye, 
  Share2, 
  Download, 
  Users, 
  Calendar,
  FileText,
  BarChart3,
  Activity,
  Database,
  PieChart,
  Target
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { DateRange } from 'react-day-picker';
import GenerationsTable from './GenerationsTable';
import PerformanceCharts from './PerformanceCharts';
import DetailedInsights from './DetailedInsights';

interface AnalyticsData {
  totalGenerations: number;
  totalViews: number;
  totalShares: number;
  totalUsers: number;
  recentActivity: any[];
  topGenerations: any[];
  userEngagement: any[];
  generationTrends: any[];
}

const GenerationAnalyticsDashboard: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date()
  });
  const [timeframe, setTimeframe] = useState('30d');

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['generation-analytics', dateRange, timeframe],
    queryFn: async () => {
      const startDate = dateRange?.from ? startOfDay(dateRange.from) : subDays(new Date(), 30);
      const endDate = dateRange?.to ? endOfDay(dateRange.to) : new Date();

      // Get total generations count
      const { data: totalGenerations } = await supabase
        .from('generations')
        .select('id', { count: 'exact' });

      // Get total views and shares
      const { data: generations } = await supabase
        .from('generations')
        .select('view_count, share_count, generation_type, created_at');

      // Get recent activity
      const { data: recentActivity } = await supabase
        .from('generation_analytics')
        .select('*, generations(title, generation_type)')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false })
        .limit(10);

      // Get top performing generations
      const { data: topGenerations } = await supabase
        .from('generations')
        .select('id, title, generation_type, view_count, share_count, created_at')
        .order('view_count', { ascending: false })
        .limit(5);

      // Get user engagement data
      const { data: userEngagement } = await supabase
        .from('generation_analytics')
        .select('user_id, event_type, created_at')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      const totalViews = generations?.reduce((sum, g) => sum + (g.view_count || 0), 0) || 0;
      const totalShares = generations?.reduce((sum, g) => sum + (g.share_count || 0), 0) || 0;
      const uniqueUsers = new Set(userEngagement?.map(e => e.user_id).filter(Boolean)).size;

      return {
        totalGenerations: totalGenerations?.length || 0,
        totalViews,
        totalShares,
        totalUsers: uniqueUsers,
        recentActivity: recentActivity || [],
        topGenerations: topGenerations || [],
        userEngagement: userEngagement || [],
        generationTrends: generations || []
      } as AnalyticsData;
    }
  });

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'view':
        return <Eye className="w-4 h-4 text-blue-500" />;
      case 'share':
        return <Share2 className="w-4 h-4 text-green-500" />;
      case 'download':
        return <Download className="w-4 h-4 text-purple-500" />;
      case 'edit':
        return <FileText className="w-4 h-4 text-orange-500" />;
      case 'duplicate':
        return <FileText className="w-4 h-4 text-yellow-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getGenerationTypeColor = (type: string) => {
    switch (type) {
      case 'survey':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'conversation':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Advanced Analytics</h2>
          <p className="text-muted-foreground">Comprehensive insights into platform performance and user engagement</p>
        </div>
        <div className="flex gap-2">
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Generations</p>
                <p className="text-3xl font-bold">{analytics?.totalGenerations || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Views</p>
                <p className="text-3xl font-bold">{analytics?.totalViews || 0}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Shares</p>
                <p className="text-3xl font-bold">{analytics?.totalShares || 0}</p>
              </div>
              <Share2 className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Users</p>
                <p className="text-3xl font-bold">{analytics?.totalUsers || 0}</p>
              </div>
              <Users className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="generations" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            All Content
          </TabsTrigger>
          <TabsTrigger value="charts" className="flex items-center gap-2">
            <PieChart className="w-4 h-4" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="insights" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Insights
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Performing Generations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Top Performing Generations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analytics?.topGenerations?.map((generation, index) => (
                    <div key={generation.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">#{index + 1}</span>
                          <h4 className="font-medium line-clamp-1">
                            {generation.title || 'Untitled Generation'}
                          </h4>
                          <Badge className={getGenerationTypeColor(generation.generation_type)}>
                            {generation.generation_type}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {generation.view_count}
                          </span>
                          <span className="flex items-center gap-1">
                            <Share2 className="w-3 h-3" />
                            {generation.share_count}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!analytics?.topGenerations?.length && (
                    <p className="text-center text-muted-foreground py-4">
                      No generations found
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analytics?.recentActivity?.map((activity) => (
                    <div key={activity.id} className="flex items-center gap-3 p-3 border rounded-lg">
                      {getEventIcon(activity.event_type)}
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {activity.event_type === 'view' && 'Viewed'}
                          {activity.event_type === 'share' && 'Shared'}
                          {activity.event_type === 'download' && 'Downloaded'}
                          {activity.event_type === 'edit' && 'Edited'}
                          {activity.event_type === 'generate' && 'Generated'}
                          {activity.event_type === 'duplicate' && 'Duplicated'}
                          {' '}{activity.generations?.title || 'generation'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(activity.created_at), 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>
                      <Badge className={getGenerationTypeColor(activity.generations?.generation_type)}>
                        {activity.generations?.generation_type}
                      </Badge>
                    </div>
                  ))}
                  {!analytics?.recentActivity?.length && (
                    <p className="text-center text-muted-foreground py-4">
                      No recent activity
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="generations">
          <GenerationsTable />
        </TabsContent>

        <TabsContent value="charts">
          <PerformanceCharts />
        </TabsContent>

        <TabsContent value="insights">
          <DetailedInsights />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GenerationAnalyticsDashboard;